import { QueryClient } from '@tanstack/react-query'
import { experimental_createQueryPersister } from '@tanstack/react-query-persist-client'
import { createTRPCClient, httpLink } from '@trpc/client'
import { createTRPCOptionsProxy } from '@trpc/tanstack-react-query'
import { get, set, del, type UseStore, createStore } from 'idb-keyval'
import { type AppRouter } from '#app/trpc/root'

function newIdbStorage(idbStore: UseStore) {
	return {
		getItem: async (key: string) => await get(key, idbStore),
		setItem: async (key: string, value: string) =>
			await set(key, value, idbStore),
		removeItem: async (key: string) => await del(key, idbStore),
	}
}

const persister = experimental_createQueryPersister({
	storage: newIdbStorage(createStore('react-query-cache', 'queries')),
	maxAge: 1000 * 60 * 60 * 24 * 7, // 1 week
	buster: import.meta.env.DEV ? new Date().toISOString() : __BUILD_TIME__,
})
const isServer = typeof window === 'undefined'

let browserQueryClient: QueryClient | undefined

export function makeQueryClient() {
	if (isServer) {
		// Server: fresh per request to avoid stale SSR data
		return new QueryClient({
			defaultOptions: {
				queries: {
					staleTime: 60_000,
					throwOnError: true,
				},
			},
		})
	}
	// Client: singleton with persistence
	if (!browserQueryClient) {
		browserQueryClient = new QueryClient({
			defaultOptions: {
				queries: {
					staleTime: 1000 * 60 * 60, // 1 hour in prod
					persister: persister.persisterFn,
					throwOnError: true,
				},
			},
		})
	}
	return browserQueryClient
}

// Keep for backward compat (tRPC proxy uses this)
const queryClient = makeQueryClient()

// Internal tRPC client (used by trpc proxy)
const trpcClient = createTRPCClient<AppRouter>({
	links: [
		httpLink({
			url: '/api/trpc',
			headers() {
				return {}
			},
		}),
	],
})

export const trpc = createTRPCOptionsProxy<AppRouter>({
	client: trpcClient,
	queryClient,
})
